import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RootState } from '../../../store';
import { useIsLoggedIn } from '../../../shared/hooks/useIsLoggedIn';
import { IJourneyResp, ISchedules } from '../../../services/journey.service';
import {
  GoogleReviewInteractionType,
  IGoogleReviewInteraction,
  IOyanaResponse,
  useSaveGoogleReviewInteractionMutation
} from '../../../services/me.service';
import { saveOyanaPersonaData } from '../../../features/me/me-slice';
import RecommendationModalUI from './Modal';

const DEFAULT_REVIEW_URL = process.env.REACT_APP_REVIEW_DEFAULT_URL || '';

const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;
const THREE_MONTHS_MS = 90 * 24 * 60 * 60 * 1000;
const SIX_MONTHS_MS = 180 * 24 * 60 * 60 * 1000;

const shouldShowBasedOnInteraction = (interaction: IGoogleReviewInteraction | null | undefined): boolean => {
  if (!interaction) return true;

  const elapsed = Date.now() - new Date(interaction.createdAt).getTime();

  switch (interaction.interactionType) {
    case 'review':
      return elapsed > ONE_YEAR_MS;
    case 'later':
      return elapsed > THREE_MONTHS_MS;
    case 'close':
      return elapsed > SIX_MONTHS_MS;
    default:
      return false;
  }
};

const getLastSchedule = (allValidJourneys: IJourneyResp[]): ISchedules | undefined => {
  return allValidJourneys
    .filter(j => j.journeyid)
    .flatMap(j => j.schedules || [])
    .filter(s => s.dateStart)
    .sort((a, b) => b.dateStart.localeCompare(a.dateStart))[0];
};

const getReviewUrl = (allValidJourneys: IJourneyResp[]): string => {
  const lastSchedule = getLastSchedule(allValidJourneys);
  if (!lastSchedule) return DEFAULT_REVIEW_URL;

  const ownerJourney = allValidJourneys.find(j => j.schedules?.some(s => s.id === lastSchedule.id));
  return ownerJourney?.clinic?.googleReviewURL || DEFAULT_REVIEW_URL;
};

const isEligibleForReview = (status: string) => {
  const disallowedStatuses = ['CANCELED', 'VOIDED'];
  return !disallowedStatuses.includes(status);
};

const RecommendationModal = () => {
  const {
    journeys: { doneJourneys, activeJourneys },
    oyana: { me }
  } = useSelector((state: RootState) => state);
  const dispatch = useDispatch();
  const isLoggedIn = useIsLoggedIn();
  const [saveInteraction] = useSaveGoogleReviewInteractionMutation();
  const [open, setOpen] = useState(false);
  const [reviewUrl, setReviewUrl] = useState(DEFAULT_REVIEW_URL);
  const shown = useRef(false);
  const { pathname } = useLocation();
  const isHome = pathname === '/';

  useEffect(() => {
    if (!isHome || !isLoggedIn || shown.current) return;
    if (!me.id) return;

    const activeLoaded = activeJourneys.length === 0 || !!activeJourneys[0]?.journeyid;
    const doneLoaded = doneJourneys.length === 0 || !!doneJourneys[0]?.journeyid;
    if (!activeLoaded || !doneLoaded) return;

    const allValidJourneys = [...activeJourneys, ...doneJourneys].filter(j => j.journeyid && isEligibleForReview(j.status));
    if (!allValidJourneys.some(j => j.journeyid)) {
      return;
    }

    if (allValidJourneys.some(j => j.schedules?.some(s => s.ratingRate === null || s.ratingRate === undefined))) {
      return;
    }

    if (allValidJourneys.some(j => j.nps && j.nps < 8)) {
      return;
    }

    if (allValidJourneys.some(j => j.schedules?.some(s => typeof s.ratingRate === 'number' && s.ratingRate < 4))) {
      return;
    }

    if (!shouldShowBasedOnInteraction(me.googleReviewInteraction)) {
      return;
    }

    setReviewUrl(getReviewUrl(allValidJourneys));
    shown.current = true;
    setOpen(true);
  }, [isHome, isLoggedIn, doneJourneys, activeJourneys, me]);

  const handleAction = async (interactionType: GoogleReviewInteractionType) => {
    setOpen(false);
    try {
      const result = await saveInteraction({ interactionType }).unwrap();
      const updated: IOyanaResponse = {
        ...me,
        googleReviewInteraction: result
      };
      dispatch(saveOyanaPersonaData(updated));
    } catch {
      // modal closed, silently fail if API call fails
    }
  };

  return (
    <RecommendationModalUI
      open={open}
      reviewUrl={reviewUrl}
      onReview={() => handleAction('review')}
      onMaybeLater={() => handleAction('later')}
      onClose={() => handleAction('close')}
    />
  );
};

export default RecommendationModal;
